import {
  getCities,
  getCountries,
  getLocalities,
  getRegions,
  getSubregions,
} from '@/api/Address/address'
import { createClient, getClients } from '@/api/Agency/agency.api'
import { Client } from '@/api/Agency/agency.interface'
import i18n from '@/i18n'
import store from '@/store'
import { Iterable, Params, VueForm } from '@/utils/interfaces'
import vRules from '@/utils/vRules'
import { computed, nextTick, Ref, ref, watch } from '@vue/composition-api'

export const clientFormComposition = (type: string, props: any) => {
  const clientDetails = ref({}) as Ref<Client>
  const clientDialog = ref(false)
  const onDetails = (item: Client) => {
    clientDetails.value = item
    clientDialog.value = true
  }
  const disableWatch = ref(false)
  const crud = ref(
    props.client || {
      reference: null,
      type: type.toUpperCase(),
      identity: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: type === 'ORIGIN' ? '+1' : '+53',
      landline: '+53',
      address: {
        address_1: '',
        address_2: '',
        country: null,
        region: null,
        subregion: null,
        locality: '',
        subregion_name: '',
        zip_code: '',
        city: null,
        city_name: '',
      },
      phone_notify: false,
      email_notify: false,
      image: '',
      credit_card: '',
      account_number: '',
    },
  ) as Ref<Client>

  watch(
    () => crud.value.address.country,
    () => {
      if (!disableWatch.value && crud.value.address.region) {
        crud.value.address.region = null
      }
    },
  )

  watch(
    () => crud.value.address.region,
    () => {
      if (!disableWatch.value) {
        if (crud.value.address.subregion) {
          crud.value.address.subregion = null
        }
        if (crud.value.address.city) {
          crud.value.address.city = null
        }
      }
    },
  )

  watch(
    () => crud.value.address.subregion,
    () => {
      if (!disableWatch.value && crud.value.address.locality) {
        crud.value.address.locality = ''
      }
    },
  )

  const getRegion = computed(() => (params?: Params) =>
    getRegions({ ...params, country: crud.value.address.country }),
  )

  const getSubregion = computed(() => (params?: Params) =>
    getSubregions({ ...params, region: crud.value.address.region }),
  )

  const getCity = computed(() => (params?: Params) =>
    getCities({ ...params, region: crud.value.address.region }),
  )

  const getLocality = computed(() => (params?: Params) =>
    getLocalities({ ...params, subregion: crud.value.address.subregion }),
  )

  const onInput = (item: Client) => {
    if (item) {
      disableWatch.value = true
      let credit_card = ''
      try {
        if (item.credit_card && item.credit_card.length === 16) {
          credit_card = `${item.credit_card.slice(
            0,
            4,
          )} ${item.credit_card.slice(4, 8)} ${item.credit_card.slice(
            8,
            12,
          )} ${item.credit_card.slice(12, 16)}`
        }
      } catch (error) {
        console.log('Error parsing card', error)
      }
      let account_number = ''
      try {
        if (item.account_number && item.account_number.length === 16) {
          account_number = `${item.account_number.slice(
            0,
            4,
          )} ${item.account_number.slice(4, 8)} ${item.account_number.slice(
            8,
            12,
          )} ${item.account_number.slice(12, 16)}`
        }
      } catch (error) {
        console.log('Error parsing account number', error)
      }
      const body = {
        ...item,
        address: item.address
          ? item.address
          : {
              address_1: '',
              address_2: '',
              country: null,
              region: null,
              subregion: null,
              subregion_name: '',
              zip_code: '',
              city: null,
              city_name: '',
            },
        credit_card,
        account_number,
      }
      crud.value = JSON.parse(JSON.stringify(body))
      setTimeout(() => {
        disableWatch.value = false
      }, 1000)
    }
  }

  const searchFields = ['identity', 'first_name', 'last_name', 'email', 'phone']

  const getClient = (params?: Params) => getClients({ ...params, type: type })

  const form = ref() as Ref<VueForm>

  const handleClient = async (client: Client): Promise<Client> => {
    const locality =
      typeof client.address.locality === 'object'
        ? (client.address.locality as { name: string }).name || ''
        : client.address.locality
    const newClient = JSON.parse(JSON.stringify(client))
    newClient.address.locality = locality
    const response = await createClient({
      ...newClient,
      account_number:
        (newClient.account_number || '').split(' ').join('') || undefined,
    })
    if (type === 'DESTINY') {
      store.commit('remittance/remittance', {
        client_destiny: response.data,
      })
    } else if (type === 'ORIGIN') {
      store.commit('remittance/remittance', {
        client_origin: response.data,
      })
    }
    return response.data
  }

  const onNext = async (clientOrigin?: string) => {
    try {
      if (type === 'DESTINY') {
        if (props.mlx) {
          if (crud.value.landline.length < 4) {
            crud.value.landline = ''
            await nextTick()
          }
          if (crud.value.phone.length < 4) {
            crud.value.phone = ''
            await nextTick()
          }
        } else {
          if (crud.value.landline.length < 4 && crud.value.phone.length >= 4) {
            crud.value.landline = ''
            await nextTick()
          }

          if (crud.value.phone.length < 4 && crud.value.landline.length >= 4) {
            crud.value.phone = ''
            await nextTick()
          }
        }
      }

      if (!form.value.validate()) return
      const params = {} as Iterable

      if (type === 'DESTINY') {
        params.reference__uuid = props.data.client_origin
      }

      const client = await getClients({
        limit: 100,
        first_name: crud.value.first_name,
        last_name: crud.value.last_name,
        ...params,
      })

      if (client.data.count) {
        if (!client.data.results.find(i => i.uuid === crud.value.uuid))
          crud.value.uuid = client.data.results[0].uuid
      } else crud.value.uuid = undefined

      if (type === 'DESTINY') {
        crud.value.reference = clientOrigin || ''
      }

      const clientResponse = await handleClient(crud.value)
      return clientResponse.uuid
    } catch (error) {
      console.log(error)
      return false
    }
  }

  const rules = {
    ...vRules,
    phoneRequired: () => {
      if (crud.value.landline.length < 4 && crud.value.phone.length < 4) {
        return i18n.t('validation.at_least_one_phone_is_required')
      } else return true
    },
  }

  return {
    clientDetails,
    clientDialog,
    crud,
    form,
    getClient,
    getCity,
    getCountries,
    getLocality,
    getRegion,
    getSubregion,
    onDetails,
    onInput,
    onNext,
    rules,
    searchFields,
  }
}
